import { ReactElement, useCallback, useMemo } from 'react';
import { LoginMethodType } from '@matchain/matchid-sdk-react/types';

import { useMatchIdConnection } from 'modules/auth/hooks/useMatchIDConnection';
import { ArrowIcon } from 'modules/common/icons';
import discordIcon from 'modules/common/icons/discord-icon.svg';
import githubIcon from 'modules/common/icons/github-icon.svg';
import googleIcon from 'modules/common/icons/google-icon.svg';
import linkedInIcon from 'modules/common/icons/linked-in-icon.svg';
import telegramIcon from 'modules/common/icons/telegram-icon.svg';
import twitterIcon from 'modules/common/icons/twitter-icon.svg';
import { useGlobalTranslation } from 'modules/i18n';

import { ConnectButton } from '../ConnectButton';
import { useStyles } from './useStyles';

interface IMatchIdLoginOptionsProps {
  onBackClick: () => void;
  onClose: () => void;
}

interface ILoginMethod {
  key: LoginMethodType;
  label: string;
  icon: string;
}

export function MatchIdLoginOptions({
  onBackClick,
  onClose,
}: IMatchIdLoginOptionsProps): ReactElement {
  const { t, keys } = useGlobalTranslation();
  const { classes } = useStyles();
  const { connect } = useMatchIdConnection();

  const loginMethods: ILoginMethod[] = useMemo(() => {
    return [
      {
        key: 'google',
        label: t(keys.socials.google),
        icon: googleIcon,
      },
      {
        key: 'telegram',
        label: t(keys.socials.telegram),
        icon: telegramIcon,
      },
      {
        key: 'twitter',
        label: t(keys.socials.twitter),
        icon: twitterIcon,
      },
      {
        key: 'discord',
        label: t(keys.socials.discord),
        icon: discordIcon,
      },
      {
        key: 'linkedin',
        label: t(keys.socials.linkedin),
        icon: linkedInIcon,
      },
      {
        key: 'github',
        label: t(keys.socials.github),
        icon: githubIcon,
      },
    ];
  }, [
    keys.socials.discord,
    keys.socials.github,
    keys.socials.google,
    keys.socials.linkedin,
    keys.socials.telegram,
    keys.socials.twitter,
    t,
  ]);

  const onLogin = useCallback(
    async (method: LoginMethodType) => {
      await connect(method);

      onClose();
    },
    [connect, onClose],
  );

  return (
    <div className={classes.root}>
      <div className={classes.options}>
        {loginMethods.map(({ key, label, icon }) => (
          <ConnectButton
            key={key}
            startIcon={
              <div className={classes.icon}>
                <img alt="" src={icon} />
              </div>
            }
            onClick={() => onLogin(key)}
          >
            {label}
          </ConnectButton>
        ))}
      </div>

      <ConnectButton onClick={onBackClick}>
        <ArrowIcon />

        <span>{t(keys.common.back)}</span>
      </ConnectButton>
    </div>
  );
}
